export interface ErrorCodeSpec {
    status: number;
    statusText: string;
}

/**
 * Feature-scoped error codes.
 * Use these with `injectError` to mint client-origin errors with stable status/statusText.
 *
 * @example
 * ```ts
 * import { injectError, DOC_UPLOAD_ERROR } from '@quipex/core/http';
 * import { throwError } from 'rxjs';
 *
 * const clientError = injectError();
 * const err = clientError.create(DOC_UPLOAD_ERROR.UploadFailed, {
 *   urnParts: ['service','document-upload','upload-key-documents']
 * });
 *
 * return throwError(() => err);
 * ```
 */

/**
 * GENERAL_ERROR: 1000-1019
 * DOC_UPLOAD_ERROR: 1020-1039
 * DOC_DOWNLOAD_ERROR: 1040-1059
 * FILE_VALIDATION_ERROR: 1060-1079
 * SHAREPOINT_VALIDATION_ERROR: 1080-1099
 * MICROSOFT_GRAPH_ERROR: 1100-1119
 * GOOGLE_MAPS_ERROR: 1120-1139
 * FORM_VALIDATION_ERROR: 1140-1159
 * FILE_PREVIEW_ERROR: 1160-1179
 * DROPBOX_AUTH_ERROR: 1180-1199
 * GOOGLE_DRIVE_AUTH_ERROR: 1200-1219
 * SPATIAL_EXPLORER_ERROR: 1220-1239
 */

export const GENERAL_ERROR = {
    ClipboardWriteFailed: {
        status: 1003,
        statusText: 'Could not copy message',
    } as const satisfies ErrorCodeSpec,
    EnvironmentConfigurationInvalid: {
        status: 1001,
        statusText: 'Environment configuration invalid',
    } as const satisfies ErrorCodeSpec,
} as const;

export const DOC_UPLOAD_ERROR = {
    PreparationFailed: {
        status: 1020,
        statusText: 'Document preparation failed',
    } as const satisfies ErrorCodeSpec,
    UploadFailed: {
        status: 1021,
        statusText: 'Document upload failed',
    } as const satisfies ErrorCodeSpec,
    IndexerFailed: {
        status: 1022,
        statusText: 'Search indexer failed',
    } as const satisfies ErrorCodeSpec,
    NoFilesProvided: {
        status: 1023,
        statusText: 'No files provided',
    } as const satisfies ErrorCodeSpec,
    ProviderConfigurationInvalid: {
        status: 1024,
        statusText: 'Document import provider is not configured',
    } as const satisfies ErrorCodeSpec,
    ProviderCredentialMissing: {
        status: 1025,
        statusText: 'Document import provider is not connected',
    } as const satisfies ErrorCodeSpec,
} as const;

export const DOC_DOWNLOAD_ERROR = {
    UrlUnavailable: {
        status: 1040,
        statusText: 'Document download URL unavailable',
    } as const satisfies ErrorCodeSpec,
    EmptyResponseBody: {
        status: 1041,
        statusText: 'Empty download response body',
    } as const satisfies ErrorCodeSpec,
    DownloadFailed: {
        status: 1042,
        statusText: 'Document download failed',
    } as const satisfies ErrorCodeSpec,
} as const;

export const FILE_VALIDATION_ERROR = {
    FileCountExceeded: {
        status: 1060,
        statusText: 'File count exceeds limit',
    } as const satisfies ErrorCodeSpec,
    InvalidFileType: {
        status: 1061,
        statusText: 'Invalid file type',
    } as const satisfies ErrorCodeSpec,
    SizeLimitExceeded: {
        status: 1062,
        statusText: 'File size exceeds limit',
    } as const satisfies ErrorCodeSpec,
} as const;

export const SHAREPOINT_VALIDATION_ERROR = {
    NoItemsSelected: {
        status: 1080,
        statusText: 'No SharePoint items selected',
    } as const satisfies ErrorCodeSpec,
    NoSiteSelected: {
        status: 1082,
        statusText: 'No SharePoint site selected',
    } as const satisfies ErrorCodeSpec,
    InvalidSite: {
        status: 1081,
        statusText: 'Invalid SharePoint site',
    } as const satisfies ErrorCodeSpec,
} as const;

export const MICROSOFT_GRAPH_ERROR = {
    ConfigurationInvalid: {
        status: 1100,
        statusText: 'Microsoft Graph configuration invalid',
    } as const satisfies ErrorCodeSpec,
    TokenFailed: {
        status: 1101,
        statusText: 'Failed to authenticate with Microsoft Graph',
    } as const satisfies ErrorCodeSpec,
    RequestFailed: {
        status: 1102,
        statusText: 'Microsoft Graph request failed',
    } as const satisfies ErrorCodeSpec,
} as const;

export const GOOGLE_MAPS_ERROR = {
    LibraryLoadFailed: {
        status: 1120,
        statusText: 'Failed to load Google Maps library',
    } as const satisfies ErrorCodeSpec,
    MapInitializationFailed: {
        status: 1121,
        statusText: 'Map initialization failed',
    } as const satisfies ErrorCodeSpec,
    MapAuthFailed: {
        status: 1122,
        statusText: 'Map authentication failed',
    } as const satisfies ErrorCodeSpec,
} as const;

export const FORM_VALIDATION_ERROR = {
    FormInvalid: {
        status: 1140,
        statusText: 'Form is invalid',
    } as const satisfies ErrorCodeSpec,
    FieldInvalid: {
        status: 1141,
        statusText: 'Required field is invalid',
    } as const satisfies ErrorCodeSpec,
    FieldMissing: {
        status: 1142,
        statusText: 'Required field is missing',
    } as const satisfies ErrorCodeSpec,
} as const;

export const FILE_PREVIEW_ERROR = {
    InvalidSourceIdentifier: {
        status: 1163,
        statusText: 'Document preview source identifier invalid',
    } as const satisfies ErrorCodeSpec,
    UrlUnavailable: {
        status: 1160,
        statusText: 'Document preview URL unavailable',
    } as const satisfies ErrorCodeSpec,
    ViewerLoadFailed: {
        status: 1161,
        statusText: 'Document preview viewer load failed',
    } as const satisfies ErrorCodeSpec,
    SearchInitializationFailed: {
        status: 1162,
        statusText: 'Document preview search initialization failed',
    } as const satisfies ErrorCodeSpec,
} as const;

export const DROPBOX_AUTH_ERROR = {
    ConfigurationInvalid: {
        status: 1180,
        statusText: 'Dropbox auth configuration invalid',
    } as const satisfies ErrorCodeSpec,
    PopupBlocked: {
        status: 1181,
        statusText: 'Dropbox auth popup blocked',
    } as const satisfies ErrorCodeSpec,
    AuthorizationFailed: {
        status: 1182,
        statusText: 'Dropbox authorization failed',
    } as const satisfies ErrorCodeSpec,
    AuthorizationTimedOut: {
        status: 1183,
        statusText: 'Dropbox authorization timed out',
    } as const satisfies ErrorCodeSpec,
    TokenExchangeFailed: {
        status: 1184,
        statusText: 'Dropbox token exchange failed',
    } as const satisfies ErrorCodeSpec,
} as const;

export const GOOGLE_DRIVE_AUTH_ERROR = {
    ConfigurationInvalid: {
        status: 1200,
        statusText: 'Google Drive auth configuration invalid',
    } as const satisfies ErrorCodeSpec,
    PopupBlocked: {
        status: 1201,
        statusText: 'Google Drive auth popup blocked',
    } as const satisfies ErrorCodeSpec,
    AuthorizationFailed: {
        status: 1202,
        statusText: 'Google Drive authorization failed',
    } as const satisfies ErrorCodeSpec,
    TokenRequestFailed: {
        status: 1203,
        statusText: 'Google Drive token request failed',
    } as const satisfies ErrorCodeSpec,
} as const;

export const SPATIAL_EXPLORER_ERROR = {
    PreviewIdentityMismatch: {
        status: 1220,
        statusText: 'Drawing preview does not match the selection',
    } as const satisfies ErrorCodeSpec,
    PreviewPending: {
        status: 1221,
        statusText: 'Drawing preview is still being prepared',
    } as const satisfies ErrorCodeSpec,
} as const;
